import React, { useEffect, useRef, useState } from 'react';
import './footer.scss';
import TrafftLogo from '@images/footer/trafft-logo.svg';
import { gsap } from "gsap";
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from "gatsby";
import ButtonLink from "../buttonLink/buttonLink";
import { getRedirectionLink, pushToDataLayer } from "../../../utils/redirectToTryForFree";
import { StaticImage } from "gatsby-plugin-image";
import Button from "../button/button";
import FooterBottom from "../footerBottom/footerBottom";

const Footer = ({ animationColor, animationStopColor, buttonType, buttonAction, buttonLabel, page }) => {
  const [strokeNum, setStrokeNum] = useState(800);
  const lineRoundRef = useRef(null);
  const url = process.env.GATSBY_WEBSITE_URL;

  gsap.registerPlugin(ScrollTrigger);

  useEffect(() => {
    if (lineRoundRef.current) {
      gsap.to(lineRoundRef.current, {
        scrollTrigger: {
          start: 'top center',
          end: 'bottom center',
          trigger: lineRoundRef.current,
        },
        strokeDashoffset: strokeNum,
        duration: 1,
        ease: 'sine.inOut',
      });
    }
  }, [strokeNum]);

  const whoIsItFor = [
    { url: '/administration-officials/', name: 'Government & Administration', internal: true },
    { url: '/education-consulting/', name: 'Education and Teaching', internal: true },
    { url: '/industries/coaching/', name: 'Coaching and Consulting', internal: true },
    { url: '/fitness-and-sports/', name: 'Sports and Training', internal: true },
    { url: '/beauty-salons/', name: 'Salons', internal: true },
    { url: '/barbershop/', name: 'Barbershop', internal: true },
    { url: '/healthcare/', name: 'Healthcare', internal: true },
    { url: '/dental-clinic/', name: 'Dental Services', internal: true },
    { url: '/personal-services/', name: 'Personal services', internal: true },
    { url: '/cleaning-services/', name: 'Cleaning services', internal: true },
    { url: '/home-services/', name: 'Home services', internal: true },
    { url: '/automotive-services/', name: 'Automotive services', internal: true }
  ];

  const products = [
    { url: '/docs/', name: 'Documentation', internal: false },
    { url: 'https://features.trafft.com/', name: 'Suggest a feature', internal: false },
    { url: '/affiliate', name: 'Become Affiliate Partner', internal: true },
    { url: url + 'blog/', name: 'Blog', internal: false },
    { url: '/about', name: 'About', internal: true },
    { url: '/contact', name: 'Contact us', internal: true },
    { url: '/investors', name: 'Investors', internal: true },
    { url: '/partners', name: 'Partners', internal: true },
    { url: '/docs/faq/', name: 'FAQ', internal: true }
  ];
  return (
    <footer
      role="presentation"
      className={`footer-wrapper ${buttonType === 'brown' && 'footer-wrapper_black'}`}
      onMouseEnter={() => setStrokeNum(0)}
    >
      <div className='footer-wrapper__cta'>
        <div className='footer-wrapper__cta__text'>
          <div className='footer-wrapper__cta__text__title' style={{fontSize:page === 'agency' ? '56px' : '64px'}}>
            {page === 'agency' ? 'Grow your revenue with white label booking software' : 'Your efficiency starts'}
            {page !== 'agency' &&
              <div className='footer-wrapper__cta__text__title__image'>
              <span className="line-round" ref={lineRoundRef}>
                <svg width="173" height="136" viewBox="0 0 173 136" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path opacity="0.4"
                        d="M79.4242 33.9239C50.798 32.9608 -5.63345 52.8376 10.6558 99.568C25.5818 142.387 102.313 129.754 137.747 111.893C199.973 80.526 156.5 0.339888 24.6729 8.18541"
                        stroke={`url(#paint0_linear_3649_7261)`} strokeWidth="14" strokeLinecap="round"/>
                  <defs>
                    <linearGradient id="paint0_linear_3649_7261" x1="-20.7928" y1="36.6604" x2="125.122" y2="-36.0575"
                                    gradientUnits="userSpaceOnUse">
                      <stop stopColor={animationColor}/>
                      <stop offset="1" stopColor={animationStopColor}/>
                    </linearGradient>
                  </defs>
                </svg>
              </span>
                <a href={`${process.env.GATSBY_SIGNUP_URL}?type=free`} target="_blank"
                   className="footer-wrapper__cta__text__title__image__here">&nbsp;here.</a>
              </div>
            }
          </div>
        </div>
        <div className='footer-wrapper__cta__button'>
          {buttonAction === 'Book a demo' ? (
            <Link to='/demo' className="tag-tracking-capterra">
              <Button
                type={buttonType ? buttonType : 'primary'}
                style={{width: '100%'}}
                label='Book a Demo'
              />
            </Link>
          ) : page === 'agency' ? (
            <ButtonLink
              href={`${process.env.GATSBY_AGENCY_SIGNUP}signup`}
              label='Start for free'
              target="_blank"
              extraClass='primary'
            />
          ) : (
            <ButtonLink
              extraClass={buttonType}
              href={getRedirectionLink()}
              onClick={pushToDataLayer}
              type='bck-transparent'
              label={buttonLabel ? buttonLabel : 'Try now for free'}
              target="_blank"
            />
          )}
        </div>
      </div>
      <div className='footer-wrapper__footer'>
        <div className='footer-wrapper__footer__contact'>
          <div className='footer-wrapper__footer__contact__trafft-logo'>
            <img src={TrafftLogo} alt='trafft' loading="lazy"/>
          </div>
          <div className='footer-wrapper__footer__contact__title'>
            Contact
          </div>
          <div className='footer-wrapper__footer__contact__list'>
            <a href='mailto:help@trafft.com' className='footer-wrapper__footer__contact__list__item'>
              help@trafft.com
            </a>
            <a href="tel:+1 302 307 2377" className='footer-wrapper__footer__contact__list__item'>
              US: +1 302 307 2377
            </a>
            <a href="tel:+44 7400 321338" className='footer-wrapper__footer__contact__list__item'>
              UK: +44 7400 321338
            </a>
            <a href="tel:+381 60 4070 370" className='footer-wrapper__footer__contact__list__item'>
              Europe: +381 60 4070 370
            </a>
          </div>
          <div className='footer-wrapper__footer__contact__social-icons'>
            <a target='_blank' rel="noreferrer" href="https://www.facebook.com/TrafftSaaS">
              <StaticImage src="../../../images/footer/facebook.svg" alt='facebook icon' loading="lazy"/>
            </a>
            <a target='_blank' rel="noreferrer" href="https://www.instagram.com/trafftsaas/">
              <StaticImage src="../../../images/footer/instagram.svg" alt='instagram icon' loading="lazy"/>
            </a>
            <a target='_blank' rel="noreferrer" href="https://twitter.com/TrafftS">
              <StaticImage src="../../../images/footer/twitter-icon.svg" alt='twitter icon' loading="lazy"/>
            </a>
            <a target='_blank' rel="noreferrer" href="https://www.linkedin.com/company/69892334">
              <StaticImage src="../../../images/footer/linkedin.svg" alt='linkedin icon' loading="lazy"/>
            </a>
            <a target='_blank' rel="noreferrer" href="https://www.youtube.com/@Trafft">
              <StaticImage src="../../../images/footer/youtube.svg" alt='youtube icon' loading="lazy"/>
            </a>
            <a target='_blank' rel="noreferrer" href="https://www.tiktok.com/@trafftbooking">
              <StaticImage src="../../../images/footer/tiktok.svg" alt='tiktok icon' loading="lazy"/>
            </a>
          </div>
        </div>
        <div className='footer-wrapper__footer__whois'>
          <div className='footer-wrapper__footer__contact__title'>
            Who’s it for
          </div>
          <div className='footer-wrapper__footer__contact__list'>
            {whoIsItFor.map((item) => (
              <a key={item.name} href={item.url} className='footer-wrapper__footer__contact__list__item'>
                {item.name}
              </a>
            ))}
          </div>
        </div>
        <div className='footer-wrapper__footer__product'>
          <div className='footer-wrapper__footer__contact__title'>
            Product
          </div>
          <div className='footer-wrapper__footer__contact__list'>
            {products.map((product) => (
              product.internal === true ? (
                <Link className='footer-wrapper__footer__contact__list__item' key={product.name} to={product.url}>
                  {product.name}
                </Link>
              ) : (
                <a key={product.name} href={product.url} className='footer-wrapper__footer__contact__list__item'>
                  {product.name}
                </a>
              )
            ))}
          </div>
        </div>
      </div>
      <FooterBottom/>
    </footer>
  );
};

export default Footer;
